import { properties } from '../api';
import { createForm, createPageHeader, type FormField } from '../components/form';
import { showToast } from '../components/toast';
import { navigate } from '../router';
import type { Property } from '../types';

export async function propertyFormPage(params: Record<string, string>): Promise<HTMLElement> {
  const page = document.createElement('div');
  const isEdit = params.id && params.id !== 'new';
  let existing: Property | null = null;

  if (isEdit) {
    try {
      existing = await properties.get(params.id);
    } catch {
      page.innerHTML = '<div class="text-red-500 p-8">Property not found.</div>';
      return page;
    }
  }

  page.appendChild(createPageHeader(isEdit ? 'Edit Property' : 'Add Property'));

  const fields: FormField[] = [
    { name: 'address', label: 'Street Address', required: true, value: existing?.address, colSpan: 2 },
    { name: 'suburb', label: 'Suburb', required: true, value: existing?.suburb },
    { name: 'state', label: 'State', type: 'select', required: true, value: existing?.state, options: [
      { value: 'VIC', label: 'VIC' }, { value: 'NSW', label: 'NSW' }, { value: 'QLD', label: 'QLD' },
      { value: 'SA', label: 'SA' }, { value: 'WA', label: 'WA' }, { value: 'TAS', label: 'TAS' },
      { value: 'NT', label: 'NT' }, { value: 'ACT', label: 'ACT' },
    ]},
    { name: 'postcode', label: 'Postcode', required: true, value: existing?.postcode },
    { name: 'estimated_value', label: 'Estimated Value ($)', type: 'number', step: '1000', value: existing?.estimated_value },
    { name: 'weekly_rent', label: 'Weekly Rent ($)', type: 'number', step: '1', required: true, value: existing?.weekly_rent },
    { name: 'tenant_name', label: 'Tenant Name', value: existing?.tenant_name },
    { name: 'lease_start_date', label: 'Lease Start', type: 'date', value: existing?.lease_start_date },
    { name: 'lease_end_date', label: 'Lease End', type: 'date', value: existing?.lease_end_date },

    // Agency
    { name: 'agency_name', label: 'Agency Name', value: existing?.agency_name },
    { name: 'agency_contact', label: 'Agency Contact', value: existing?.agency_contact },
    { name: 'agency_email', label: 'Agency Email', type: 'email', value: existing?.agency_email },
    { name: 'agent_company', label: 'Agent Company', value: existing?.agent_company },
    { name: 'agent_name', label: 'Agent Name', value: existing?.agent_name },
    { name: 'agent_phone', label: 'Agent Phone', type: 'tel', value: existing?.agent_phone },
    { name: 'agent_emails', label: 'Agent Emails (comma-separated)', value: existing?.agent_emails?.join(', '), colSpan: 2, helpText: 'Used for forwarding rules to detect which property an email relates to' },
    { name: 'management_fee_percent', label: 'Management Fee %', type: 'number', step: '0.1', value: existing?.management_fee_percent },
    { name: 'leasing_fee_percent', label: 'Leasing Fee %', type: 'number', step: '0.1', value: existing?.leasing_fee_percent },
    { name: 'leasing_fee_amount', label: 'Leasing Fee Amount ($)', type: 'number', step: '0.01', value: existing?.leasing_fee_amount },

    // Insurance
    { name: 'insurance_company', label: 'Insurance Company', value: existing?.insurance_company },
    { name: 'insurance_policy_number', label: 'Policy Number', value: existing?.insurance_policy_number },
    { name: 'insurance_annual_cost', label: 'Annual Premium ($)', type: 'number', step: '0.01', value: existing?.insurance_annual_cost },
    { name: 'insurance_renewal_date', label: 'Insurance Renewal', type: 'date', value: existing?.insurance_renewal_date },

    // Compliance
    { name: 'gas_safety_expiry', label: 'Gas Safety Expiry', type: 'date', value: existing?.gas_safety_expiry },
    { name: 'gas_safety_provider', label: 'Gas Safety Provider', value: existing?.gas_safety_provider },
    { name: 'electrical_safety_expiry', label: 'Electrical Safety Expiry', type: 'date', value: existing?.electrical_safety_expiry },
    { name: 'electrical_safety_provider', label: 'Electrical Provider', value: existing?.electrical_safety_provider },
    { name: 'smoke_alarm_expiry', label: 'Smoke Alarm Expiry', type: 'date', value: existing?.smoke_alarm_expiry },
    { name: 'smoke_alarm_provider', label: 'Smoke Alarm Provider', value: existing?.smoke_alarm_provider },

    { name: 'notes', label: 'Notes', type: 'textarea', value: existing?.notes, colSpan: 2 },
  ];

  const form = createForm(fields, async (data) => {
    // Parse agent_emails from comma-separated string
    const parsed: Partial<Property> = {
      ...data,
      estimated_value: parseFloat(data.estimated_value) || 0,
      weekly_rent: parseFloat(data.weekly_rent) || 0,
      management_fee_percent: parseFloat(data.management_fee_percent) || 0,
      leasing_fee_percent: parseFloat(data.leasing_fee_percent) || 0,
      leasing_fee_amount: parseFloat(data.leasing_fee_amount) || 0,
      insurance_annual_cost: parseFloat(data.insurance_annual_cost) || 0,
      agent_emails: data.agent_emails ? data.agent_emails.split(',').map((e: string) => e.trim()).filter(Boolean) : [],
    } as Partial<Property>;

    try {
      if (isEdit) {
        await properties.update(params.id, parsed);
        showToast('Property updated', 'success');
        navigate(`#/properties/${params.id}`);
      } else {
        const newProp = await properties.create(parsed);
        showToast('Property created', 'success');
        navigate(`#/properties/${newProp.id}`);
      }
    } catch {
      showToast('Failed to save property', 'error');
    }
  }, isEdit ? 'Update Property' : 'Create Property', {
    cancelAction: () => navigate(isEdit ? `#/properties/${params.id}` : '#/properties'),
  });

  page.appendChild(form);
  return page;
}
